import type { ExtendedUserResponseSchema } from '@/types/user.types';
import { isSupervisor } from '@/services/userService';
import type { HIInvoiceStatus } from '@/types/homeInspection.types';

/**
 * Product decisions for HI invoice parity (hi-invoice-parity SDD):
 * - Q3 Edit/Confirm roles: ADMIN, OPERATOR, or team supervisor (PS-aligned).
 * - Q5 Feature flag: reuse Feature.HOME_INSPECTION (no dedicated invoice flag yet).
 * - Q2 billDate: set on confirm (with confirmedAt); UI shows "Invoice not confirmed" until then.
 * - Q4 Context: frontend best-effort; no fetch-for-edit lock for read context.
 * - Status flow (PS parity): DRAFT → REVIEWED (on billing edit) → CONFIRMED → PAID.
 * - Apply Payment: gated by Feature accounting.applyPayment, ADMIN/ACCOUNTANT only (AR parity).
 */

const canManageHiInvoice = (user: ExtendedUserResponseSchema): boolean =>
  ['ADMIN', 'OPERATOR'].includes(user.roleId) || isSupervisor(user);

export const canEditHiInvoice = (
  user: ExtendedUserResponseSchema | null | undefined,
  status: HIInvoiceStatus | undefined
): boolean => {
  if (!user || (status !== 'DRAFT' && status !== 'REVIEWED')) return false;
  return canManageHiInvoice(user);
};

export const canConfirmHiInvoice = (
  user: ExtendedUserResponseSchema | null | undefined,
  status: HIInvoiceStatus | undefined
): boolean => {
  if (!user || status === "CONFIRMED" || status === "PAID") return false;
  return canManageHiInvoice(user);
};

export const canApplyPaymentToHiInvoice = (
  user: ExtendedUserResponseSchema | null | undefined,
  status: HIInvoiceStatus | undefined
): boolean => {
  if (!user || status !== 'CONFIRMED') return false;
  return ['ADMIN', 'ACCOUNTANT'].includes(user.roleId);
};
